import React, { useState } from 'react';
import { Download, ChevronDown, MessageCircle, Mail, Share2, Loader2, Paperclip, Copy } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { downloadPdf, sharePdfFile, canSharePdfFile, slugify } from '../lib/pdf';
import { toast } from 'sonner';

// Split button: [ Download PDF | v ]  with WhatsApp / Email share options.
// `targetRef` must point at the DOM node holding the printable document.
export default function ShareDocActions({
  targetRef, docLabel = 'Document', number = '', recipientName = '',
  recipientEmail = '', recipientPhone = '', amountText = '', companyName = '', className = '',
}) {
  const [busy, setBusy] = useState(false);
  const [attachHint, setAttachHint] = useState(null);

  const fileName = `${slugify(docLabel)}-${slugify(number)}.pdf`;
  const greeting = recipientName ? `Hi ${recipientName},` : 'Hello,';
  const amountPart = amountText ? ` amounting to ${amountText}` : '';
  const subject = `${docLabel} ${number} from ${companyName}`;
  const message =
    `${greeting}\n\nPlease find attached your ${docLabel} ${number} from ${companyName}${amountPart}.\n\n` +
    `Kindly review and let us know if you have any questions.\n\nThank you,\n${companyName}`;

  const makePdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(targetRef?.current, fileName);
      return true;
    } catch (e) {
      toast.error('Could not generate the PDF. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePdf = async () => {
    if (await makePdf()) toast.success(`Downloaded ${fileName}`);
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      await sharePdfFile(targetRef?.current, fileName, { title: subject, text: message });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Sharing was not completed');
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsApp = async () => {
    if (canSharePdfFile()) { await nativeShare(); return; }
    if (!(await makePdf())) return;
    const phone = (recipientPhone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    setAttachHint({ channel: 'WhatsApp' });
  };

  const emailVia = async (provider) => {
    if (!(await makePdf())) return;
    const to = recipientEmail || '';
    const s = encodeURIComponent(subject);
    const b = encodeURIComponent(message);
    const urls = {
      default: `mailto:${to}?subject=${s}&body=${b}`,
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${s}&body=${b}`,
      outlook: `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(to)}&subject=${s}&body=${b}`,
      yahoo: `https://compose.mail.yahoo.com/?to=${encodeURIComponent(to)}&subject=${s}&body=${b}`,
    };
    if (provider === 'default') window.location.href = urls.default;
    else window.open(urls[provider], '_blank', 'noopener,noreferrer');
    setAttachHint({ channel: 'email' });
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(message); toast.success('Message copied'); }
    catch (e) { toast.error('Could not copy message'); }
  };

  return (
    <div className={`inline-flex ${className}`} data-testid="share-doc-actions">
      <Button onClick={savePdf} disabled={busy} className="rounded-r-none bg-blue-600 hover:bg-blue-700" data-testid="download-pdf-btn">
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download PDF
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="rounded-l-none border-l border-blue-500 bg-blue-600 px-2 hover:bg-blue-700" aria-label="More share options" data-testid="share-options-trigger">
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel>Send {docLabel} {number}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={savePdf} className="cursor-pointer" data-testid="menu-download-pdf">
            <Download className="mr-2 h-4 w-4 text-slate-500" /> Download as PDF
          </DropdownMenuItem>
          {canSharePdfFile() && (
            <DropdownMenuItem onClick={nativeShare} className="cursor-pointer" data-testid="menu-native-share">
              <Share2 className="mr-2 h-4 w-4 text-indigo-600" /> Share PDF file…
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={shareWhatsApp} className="cursor-pointer" data-testid="menu-share-whatsapp">
            <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> Share via WhatsApp
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="menu-share-email">
              <Mail className="mr-2 h-4 w-4 text-blue-600" /> Send via Email
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-52">
              <DropdownMenuItem onClick={() => emailVia('default')} className="cursor-pointer" data-testid="menu-email-default">Default mail app</DropdownMenuItem>
              <DropdownMenuItem onClick={() => emailVia('gmail')} className="cursor-pointer" data-testid="menu-email-gmail">Gmail (web)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => emailVia('outlook')} className="cursor-pointer" data-testid="menu-email-outlook">Outlook (web)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => emailVia('yahoo')} className="cursor-pointer" data-testid="menu-email-yahoo">Yahoo Mail</DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={copyMessage} className="cursor-pointer" data-testid="menu-copy-message">
            <Copy className="mr-2 h-4 w-4 text-slate-500" /> Copy message text
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!attachHint} onOpenChange={(v) => !v && setAttachHint(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="attach-hint-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip className="h-5 w-5 text-blue-600" /> Attach the PDF</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Your {docLabel} was downloaded as:</p>
            <p className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-slate-800" data-testid="attach-hint-filename">{fileName}</p>
            <p>
              A {attachHint?.channel === 'WhatsApp' ? 'WhatsApp chat' : 'compose window'} has been opened with your message ready.
              Attach the downloaded file from your <span className="font-medium">Downloads</span> folder to send it.
            </p>
            <p className="text-xs text-slate-400">Browsers do not allow websites to attach files automatically, so this one tap keeps you in control.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={savePdf} data-testid="attach-hint-redownload">Download again</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setAttachHint(null)} data-testid="attach-hint-done">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
